import { useCallback } from 'react';
import { compress } from '@thaparoyal/image-compression';

interface ImageCompressionOptions {
    /**
     * Maximum width for the compressed image.
     * If the original image is smaller, it won't be upscaled.
     */
    maxWidth?: number;
    /**
     * Maximum height for the compressed image.
     * If the original image is smaller, it won't be upscaled.
     */
    maxHeight?: number;
    /**
     * Quality of the compressed image (0-1).
     */
    quality?: number;
    /**
     * Output format for the compressed image.
     * Default: preserves original format
     */
    outputFormat?: 'image/jpeg' | 'image/png' | 'image/webp';
    /**
     * Minimum file size in MB to trigger compression.
     * Compression will only occur if file size exceeds this value,
     * or if image dimensions exceed maxWidth/maxHeight.
     * Default: 10
     */
    shouldCompressSizeMB?: number;
    /**
     * Whether to enable debug mode
     */
    debug?: boolean;
}

interface CompressedImageResult {
    /**
     * The compressed image as a File object
     */
    file: File;
    /**
     * Original file size in bytes
     */
    originalSize: number;
    /**
     * Compressed file size in bytes
     */
    compressedSize: number;
    /**
     * Compression ratio (0-1, where 0.5 means 50% size reduction)
     */
    compressionRatio: number;
    /**
     * Whether compression was actually performed
     */
    wasCompressed: boolean;
}

const DEFAULT_IMAGE_COMPRESSION_OPTIONS: ImageCompressionOptions = {
    maxWidth: 4032, // 12 megapixels image
    maxHeight: 4032,
    quality: 0.98,
    outputFormat: 'image/jpeg',
    shouldCompressSizeMB: 10,
    debug: false,
};

/**
 * Gets image dimensions from a file.
 */
const getImageDimensions = (
    file: File
): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = reject;
            img.src = e.target?.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Compresses a single image file.
 * This is a standalone utility function that can be used outside of React components.
 *
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise resolving to compression result
 */
export const compressSingleImage = async (
    file: File,
    options: ImageCompressionOptions = {}
): Promise<CompressedImageResult> => {
    const originalSize = file.size;
    // Merge provided options with defaults
    const mergedOptions = {
        ...DEFAULT_IMAGE_COMPRESSION_OPTIONS,
        ...options,
    };
    const shouldCompressSizeMB = mergedOptions.shouldCompressSizeMB!;
    const shouldCompressSizeBytes = shouldCompressSizeMB * 1024 * 1024;
    const maxWidth = mergedOptions.maxWidth!;
    const maxHeight = mergedOptions.maxHeight!;

    // Check if compression is needed based on file size
    const needsCompressionBySize = originalSize > shouldCompressSizeBytes;

    // Check if compression is needed based on dimensions
    let needsCompressionByDimensions = false;
    try {
        const { width, height } = await getImageDimensions(file);
        needsCompressionByDimensions = width > maxWidth || height > maxHeight;
    } catch (error) {
        console.error('Failed to get image dimensions:', error);
        // If we can't get dimensions, proceed with compression if size check passes
    }

    // check if compression is needed based on file type
    const needsCompressionByFileType =
        file.type === 'image/avif' ||
        file.type === 'image/heic' ||
        file.type === 'image/jxl';

    // Skip compression if not needed
    if (
        !needsCompressionBySize &&
        !needsCompressionByDimensions &&
        !needsCompressionByFileType
    ) {
        if (mergedOptions.debug) {
            console.log('🚀 ~ useImageCompression : No compression needed', {
                originalSize,
                compressedSize: originalSize,
                compressionRatio: 1,
                wasCompressed: false,
                file,
            });
        }
        return {
            file,
            originalSize,
            compressedSize: originalSize,
            compressionRatio: 1,
            wasCompressed: false,
        };
    }

    try {
        // Map MIME type to preferred format
        const getPreferredFormat = (
            mimeType: string
        ): 'webp' | 'jpeg' | 'png' | 'avif' => {
            // Always convert AVIF, HEIC, and JXL to JPEG
            if (
                mimeType === 'image/avif' ||
                mimeType === 'image/heic' ||
                mimeType === 'image/jxl'
            ) {
                return 'jpeg';
            }

            if (mergedOptions.outputFormat) {
                const formatMap: Record<
                    string,
                    'webp' | 'jpeg' | 'png' | 'avif'
                > = {
                    'image/jpeg': 'jpeg',
                    'image/png': 'png',
                    'image/webp': 'webp',
                    'image/avif': 'jpeg',
                };
                return formatMap[mergedOptions.outputFormat] || 'jpeg';
            }
            // Auto-detect from file type
            if (mimeType.includes('png')) return 'png';
            if (mimeType.includes('webp')) return 'webp';
            return 'jpeg'; // Default fallback
        };

        // Compression options using merged defaults
        const compressionOptions = {
            maxWidth,
            maxHeight,
            quality: mergedOptions.quality!,
            preferredFormat: getPreferredFormat(file.type),
            maxSizeMB: mergedOptions.shouldCompressSizeMB,
            preserveExif: true,
            debug: mergedOptions.debug,
        };

        // Perform compression (returns File directly)
        const compressedFile = await compress(file, compressionOptions);

        const compressedSize = compressedFile.size;
        const compressionRatio = compressedSize / originalSize;

        // Only use compressed version if it's actually smaller
        const shouldUseCompressed =
            compressedSize < originalSize || needsCompressionByFileType;
        if (mergedOptions.debug) {
            console.log('🚀 ~ useImageCompression completed:', {
                originalSize,
                compressedSize,
                compressionRatio,
                shouldUseCompressed,
                compressionOptions,
                compressedFile,
                needsCompressionByFileType,
                file,
            });
        }
        // change compressedFile filename by add suffix -compressed
        // If format changed (e.g., AVIF → JPEG), use the new format's extension
        const getExtensionFromMimeType = (mimeType: string): string => {
            if (mimeType === 'image/jpeg') return 'jpg';
            if (mimeType === 'image/png') return 'png';
            if (mimeType === 'image/webp') return 'webp';
            if (mimeType === 'image/avif') return 'avif';
            // Fallback to original extension if MIME type not recognized
            return file.name.split('.').pop() || '';
        };

        const originalExtension = file.name.split('.').pop() || '';
        const newExtension = getExtensionFromMimeType(compressedFile.type);
        const baseFileName = file.name.replace(/\.[^/.]+$/, '');
        const fileNameSuffix = 'ap-compressed';

        // Use new extension if format changed, otherwise keep original extension
        const finalExtension = needsCompressionByFileType
            ? newExtension
            : originalExtension;
        const compressedFileName = finalExtension
            ? `${baseFileName}-${fileNameSuffix}.${finalExtension}`
            : `${baseFileName}-${fileNameSuffix}`;
        const compressedFileWithNewName = new File(
            [compressedFile],
            compressedFileName,
            {
                type: compressedFile.type,
                lastModified: Date.now(),
            }
        );

        return {
            file: shouldUseCompressed ? compressedFileWithNewName : file,
            originalSize,
            compressedSize: shouldUseCompressed ? compressedSize : originalSize,
            compressionRatio: shouldUseCompressed ? compressionRatio : 1,
            wasCompressed: shouldUseCompressed,
        };
    } catch (error) {
        console.error('Image compression failed:', error);
        // Return original file if compression fails
        return {
            file,
            originalSize,
            compressedSize: originalSize,
            compressionRatio: 1,
            wasCompressed: false,
        };
    }
};

/**
 * Custom hook for compressing images before upload.
 * This hook provides client-side image compression to reduce file sizes
 * before sending to the backend.
 *
 * @example
 * const { compressImages } = useImageCompression();
 *
 * const handleUpload = async (files: File[]) => {
 *   const results = await compressImages(files, {
 *     maxWidth: 1920,
 *     maxHeight: 1080,
 *     quality: 0.9
 *   });
 *   // Upload the compressed files
 *   uploadToServer(results.map(r => r.file));
 * };
 */
export const useImageCompression = () => {
    const compressSingleImageCallback = useCallback(
        async (
            file: File,
            options: ImageCompressionOptions = {}
        ): Promise<CompressedImageResult> => {
            return compressSingleImage(file, options);
        },
        []
    );

    /**
     * Compresses multiple image files in parallel.
     *
     * @param files - Array of image files to compress
     * @param options - Compression options
     * @returns Promise resolving to array of compression results
     */
    const compressImages = useCallback(
        async (
            files: File[],
            options: ImageCompressionOptions = {}
        ): Promise<CompressedImageResult[]> => {
            const compressionPromises = files.map((file) =>
                compressSingleImageCallback(file, options)
            );

            return await Promise.all(compressionPromises);
        },
        [compressSingleImageCallback]
    );

    /**
     * Compresses a FileList (from input element) and returns an array of results.
     *
     * @param fileList - FileList from input element
     * @param options - Compression options
     * @returns Promise resolving to array of compression results
     */
    const compressFileList = useCallback(
        async (
            fileList: FileList,
            options: ImageCompressionOptions = {}
        ): Promise<CompressedImageResult[]> => {
            const filesArray = Array.from(fileList);
            return await compressImages(filesArray, options);
        },
        [compressImages]
    );

    return {
        compressSingleImage: compressSingleImageCallback,
        compressImages,
        compressFileList,
    };
};
